import { create } from "zustand";
import { modalType, ModelIndexes } from "../types/addContent";
import { getSharePricePerformanceData } from "../utils/sharePricePerformance/getSharePricePerformanceData";

export interface MetaData {
  fontSize?: number;
  fontColor?: string;
  fontStyle?: string;
  fontsSection?: {
    slideTitle: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
    slideSubTitle: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
    keyTakeAways: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
    placeholderTitle: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
    placeholderSubTitle: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
    placeholderAxisLabels: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
    source: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
    footnotes: {
      style: string;
      size: string;
      color: string;
      bold: boolean;
      italic: boolean;
      underline: boolean;
    };
  };
  sourceData?: {
    enabled: boolean;
    value: string;
    positioning: string;
  };
  notesData?: {
    enabled: boolean;
    value: string[];
    positioning: string;
  };
  titlePlaceholder?: any;
  subTitlePlaceholder?: any;
  subtitleRight?: boolean;
  chartTitleData?: {
    enabled: boolean;
    value: string;
  };
  chartSubTitleData?: {
    enabled: boolean;
    value: string;
  };
  alphaScriptSettings?: {
    workbookGuid: string | null;
    linkId: string | null;
    chartName: string | null;
    render: string | null;
    generated: string | null;
  } | null;
  excelFileUrl?: string | null;
}

interface StoreState {
  errors: any[];
  company: any[];
  currencyOptions: any[];
  models: any[];
  completeFormatDataRef: any[];
  formCompleted: boolean;
  currentStep: number;
  timePeriod: [string, string] | null;
  openModelType: modalType;
  summaryTable: any[];
  shadingPeriodTable: any[];
  verticalLineTable: any[];
  callOutBoxTable: any[];
  APALCompany: any[];
  APALCompanyIndex: number;
  isLoaded: boolean;
  metaData: MetaData;
  templateSearchVisible: boolean;
  currentPrimaryTemplate: any | null;

  setErrors: (errors: any[]) => void;
  setCompany: (company: any[]) => void;
  setCurrencyOptions: (currencyOptions: any[]) => void;
  setModels: (models: any[] | ((prevModels: any[]) => any[])) => void;
  setCompleteFormatData: (data: any[]) => void;
  setFormCompleted: (value: boolean) => void;
  setCurrentStep: (step: number) => void;
  setTimePeriod: (period: [string, string] | null) => void;
  setOpenModelType: (openModelType: modalType) => void;
  setSummaryTable: (data: any[]) => void;
  setShadingPeriodTable: (data: any[]) => void;
  setVerticalLineTable: (data: any[]) => void;
  setCallOutBoxTable: (data: any[]) => void;
  setAPALCompany: (data: any[]) => void;
  setAPALCompanyIndex: (data: number) => void;
  setIsLoaded: (isLoaded: boolean) => void;
  setMetaData: (data: Partial<MetaData>) => void;
  setTemplateSearchVisible: (templateSearchVisible: boolean) => void;
  setCurrentPrimaryTemplate: (currentPrimaryTemplate: any | null) => void;
}

export const useSharePricePerformanceStore = create<StoreState>((set, get) => {
  const { dataSource, oldModelsData } = getSharePricePerformanceData();

  return {
    errors: [],
    company: [],
    models: [],
    completeFormatDataRef: [],
    currencyOptions: [],
    formCompleted: false,
    currentStep:
      (dataSource && oldModelsData.length) || localStorage.getItem("changeWindow")
        ? ModelIndexes.FORMAT_TEMPLATE
        : ModelIndexes.CONTENT_LIBRARY,
    timePeriod: null,
    openModelType: null,
    summaryTable: [],
    shadingPeriodTable: [],
    verticalLineTable: [],
    callOutBoxTable: [],
    APALCompany: [],
    APALCompanyIndex: 0,
    isLoaded: false,
    metaData: {},
    templateSearchVisible: false,
    currentPrimaryTemplate: null,

    setErrors: (errors) => set({ errors }),
    setCompany: (company) => set({ company }),
    setCurrencyOptions: (currencyOptions) => set({ currencyOptions }),
    setModels: (models) => {
      const current = get().models;
      const updatedModels = typeof models === "function" ? models(current) : models;
      set({ models: updatedModels });
    },
    setCompleteFormatData: (data) => set({ completeFormatDataRef: data }),
    setFormCompleted: (value) => set({ formCompleted: value }),
    setCurrentStep: (step) => set({ currentStep: step }),
    setTimePeriod: (period) => set({ timePeriod: period }),
    setOpenModelType: (openModelType) => set({ openModelType }),
    setSummaryTable: (data) => set({ summaryTable: data }),
    setShadingPeriodTable: (data) => set({ shadingPeriodTable: data }),
    setVerticalLineTable: (data) => set({ verticalLineTable: data }),
    setCallOutBoxTable: (data) => set({ callOutBoxTable: data }),
    setAPALCompany: (data) => set({ APALCompany: data }),
    setAPALCompanyIndex: (data) => set({ APALCompanyIndex: data }),
    setIsLoaded: (data) => set({ isLoaded: data }),
    setMetaData: (data: Partial<MetaData>) =>
      set((state) => ({
        metaData: {
          ...state.metaData,
          ...data,
        },
      })),
    setTemplateSearchVisible: (templateSearchVisible) => set({ templateSearchVisible }),
    setCurrentPrimaryTemplate: (currentPrimaryTemplate) => set({ currentPrimaryTemplate }),
  };
});
